import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { get_bearer_audit_log } from '../tools-network';

const ACTION_LABELS = {
  BEARER_CREATED: 'Bärare skapad',
  BEARER_UPDATED: 'Bärare uppdaterad',
  BEARER_AND_DISEASES_UPDATED: 'Bärare & diagnoser uppdaterade',
  BEARER_DELETED: 'Bärare borttagen',
  DISEASES_UPDATED: 'Diagnoser uppdaterade',
  EMERGENCY_CONTACT_ADDED: 'Nödkontakt tillagd',
  EMERGENCY_CONTACT_UPDATED: 'Nödkontakt uppdaterad',
  EMERGENCY_CONTACT_DELETED: 'Nödkontakt borttagen',
};

const ACTION_COLORS = {
  BEARER_CREATED: 'bg-green-100 text-green-800',
  BEARER_UPDATED: 'bg-blue-100 text-blue-800',
  BEARER_AND_DISEASES_UPDATED: 'bg-purple-100 text-purple-800',
  BEARER_DELETED: 'bg-red-100 text-red-800',
  DISEASES_UPDATED: 'bg-green-100 text-green-800',
  EMERGENCY_CONTACT_ADDED: 'bg-yellow-100 text-yellow-800',
  EMERGENCY_CONTACT_UPDATED: 'bg-yellow-100 text-yellow-800',
  EMERGENCY_CONTACT_DELETED: 'bg-orange-100 text-orange-800',
};

function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('sv-SE', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatDetail(detail) {
  if (!detail || typeof detail !== 'object') return null;
  const entries = Object.entries(detail).filter(([k]) => k !== 'updated');
  if (entries.length === 0) return null;
  return entries.map(([field, change]) => {
    if (change && typeof change === 'object' && 'old' in change && 'new' in change) {
      const oldVal = change.old === null || change.old === undefined ? '—' : String(change.old);
      const newVal = change.new === null || change.new === undefined ? '—' : String(change.new);
      return (
        <div key={field} className="text-xs text-gray-600 ml-2">
          <span className="font-medium">{field}:</span>{' '}
          <span className="line-through text-red-500">{oldVal}</span>{' → '}
          <span className="text-green-600 font-medium">{newVal}</span>
        </div>
      );
    }
    return (
      <div key={field} className="text-xs text-gray-600 ml-2">
        <span className="font-medium">{field}:</span> {JSON.stringify(change)}
      </div>
    );
  });
}

function SnapshotDetails({ snapshot }) {
  const [expanded, setExpanded] = useState(false);
  if (!snapshot) return null;

  return (
    <div className="mt-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-cyan-600 hover:text-cyan-800 underline"
      >
        {expanded ? 'Dölj ögonblicksbild ▲' : 'Visa ögonblicksbild ▼'}
      </button>
      {expanded && (
        <div className="mt-2 bg-gray-50 rounded-md p-3 text-xs space-y-2 border border-gray-200">
          {/* Bearer info */}
          <div>
            <span className="font-semibold">Bärare:</span>{' '}
            {snapshot.bearer?.name || '—'} | Verbal: {snapshot.bearer?.is_verbal ? 'Ja' : 'Nej'} |
            Födelseår: {snapshot.bearer?.birth_year || '—'} | Kön: {snapshot.bearer?.sex || '—'} |
            Blodgrupp: {snapshot.bearer?.blood_type || '—'}
          </div>
          {/* Diseases */}
          {snapshot.diseases && snapshot.diseases.length > 0 && (
            <div>
              <span className="font-semibold">Diagnoser:</span>
              <ul className="list-disc ml-4">
                {snapshot.diseases.map((d, i) => (
                  <li key={i}>{d.disease_text}{d.user_details ? ` — ${d.user_details}` : ''}</li>
                ))}
              </ul>
            </div>
          )}
          {/* Emergency contacts */}
          {snapshot.emergency_contacts && snapshot.emergency_contacts.length > 0 && (
            <div>
              <span className="font-semibold">Nödkontakter:</span>
              <ul className="list-disc ml-4">
                {snapshot.emergency_contacts.map((c, i) => (
                  <li key={i}>{c.phone} — {c.description || 'Ingen beskrivning'}{c.is_primary ? ' (Primär)' : ''}</li>
                ))}
              </ul>
            </div>
          )}
          {/* User */}
          {snapshot.user && (
            <div>
              <span className="font-semibold">Användare:</span>{' '}
              {snapshot.user.name || '—'} (ID: {snapshot.user.user_id || '—'})
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const BearerAuditLog = ({ loginToken, bearerId }) => {
  const { t } = useTranslation();
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const fetchAuditLog = async () => {
    if (!bearerId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await get_bearer_audit_log(loginToken, bearerId);
      if (res.success) {
        setAuditLog(res.data || []);
      } else {
        setError(res.msg || 'Kunde inte hämta ändringshistorik');
      }
    } catch (err) {
      setError('Kunde inte hämta ändringshistorik');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && bearerId) {
      fetchAuditLog();
    }
  }, [isOpen, bearerId]);

  return (
    <div className="mt-6 border-2 border-amber-200 rounded-lg p-4 bg-amber-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-left"
      >
        <h4 className="text-sm font-semibold text-amber-900 flex items-center">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Ändringshistorik
        </h4>
        <svg className={`w-5 h-5 text-amber-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="mt-3">
          {loading && <p className="text-sm text-gray-500">Laddar...</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!loading && !error && auditLog.length === 0 && (
            <p className="text-sm text-gray-500 italic">Ingen ändringshistorik finns ännu.</p>
          )}
          {!loading && !error && auditLog.length > 0 && (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {auditLog.map((entry, idx) => (
                <div key={idx} className="bg-white rounded-md p-3 shadow-sm border border-gray-100">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[entry.action] || 'bg-gray-100 text-gray-800'}`}>
                        {ACTION_LABELS[entry.action] || entry.action}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        {formatTimestamp(entry.timestamp)}
                      </span>
                    </div>
                    {entry.changed_by && (
                      <span className="text-xs text-gray-400">
                        av {entry.changed_by.user_name || `#${entry.changed_by.user_id}`}
                      </span>
                    )}
                  </div>
                  {formatDetail(entry.detail)}
                  <SnapshotDetails snapshot={entry.snapshot} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BearerAuditLog;
